import { BASEURL } from './constant';

export let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

const REFRESH_KEY = 'refreshToken';
const REFRESH_SKEW_SECONDS = 60;

export type LoginRespData = {
    firstname: string;
    lastname: string;
    role: string;
    email: string;
    access_token: string;
    id: string | number;
};

export type TokenPayload = {
    expires?: number;
    exp?: number;
    user_id?: string | number;
};

export const getAccessToken = () => {
    if (!accessToken) {
        accessToken = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken') || null;
        if (accessToken && !sessionStorage.getItem('accessToken')) {
            sessionStorage.setItem('accessToken', accessToken);
            localStorage.removeItem('accessToken');
        }
    }
    return accessToken;
};

export const getRefreshToken = (): string | null => {
    return sessionStorage.getItem(REFRESH_KEY);
};

/** Decode JWT payload without verifying signature (client-side expiry check only). */
export const decodeTokenPayload = (token: string): TokenPayload | null => {
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json);
    } catch {
        return null;
    }
};

export const getAccessTokenExpiresAt = (): number | null => {
    const token = getAccessToken();
    if (!token) return null;
    const payload = decodeTokenPayload(token);
    if (!payload) return null;
    const exp = payload.exp ?? payload.expires;
    if (exp == null) return null;
    return Number(exp);
};

export const secondsUntilExpiry = (): number => {
    const exp = getAccessTokenExpiresAt();
    if (exp == null) return -1;
    return exp - Date.now() / 1000;
};

export const isAccessTokenValid = (): boolean => {
    return secondsUntilExpiry() > 0;
};

export const login = (data: LoginRespData, access_token: string, refresh_token?: string) => {
    localStorage.setItem('userName', `${data.lastname}, ${data.firstname}`);
    localStorage.setItem('email', `${data.email}`);
    localStorage.setItem('role', data.role);
    localStorage.setItem('userid', String(data.id));
    accessToken = access_token;
    sessionStorage.setItem('accessToken', access_token);
    localStorage.removeItem('accessToken');
    if (refresh_token) {
        sessionStorage.setItem(REFRESH_KEY, refresh_token);
    }
};

export const logout = async (callServer = false) => {
    const refresh = getRefreshToken();
    const token = getAccessToken();
    if (callServer && token) {
        try {
            await fetch(`${BASEURL}/auth/logout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ refresh_token: refresh }),
            });
        } catch {
            // ignore logout network errors
        }
    }
    accessToken = null;
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('role');
    localStorage.removeItem('email');
    localStorage.removeItem('userid');
};

/** Clear session and send user to login when token is missing/expired or API rejects auth. */
export const handleUnauthorized = (reason: string = 'session_expired') => {
    void logout(false);
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = `/login?reason=${encodeURIComponent(reason)}`;
    }
};

async function refreshAccessToken(): Promise<boolean> {
    const refresh = getRefreshToken();
    if (!refresh) return false;
    try {
        const response = await fetch(`${BASEURL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refresh }),
        });
        if (!response.ok) return false;
        const data = await response.json();
        if (!data?.access_token) return false;
        accessToken = data.access_token;
        sessionStorage.setItem('accessToken', data.access_token);
        if (data.refresh_token) {
            sessionStorage.setItem(REFRESH_KEY, data.refresh_token);
        }
        return true;
    } catch {
        return false;
    }
}

/** Ensure access token is valid; refresh if near expiry. Returns false if session cannot be restored. */
export const ensureSession = async (): Promise<boolean> => {
    const remaining = secondsUntilExpiry();
    if (remaining > REFRESH_SKEW_SECONDS) {
        return true;
    }
    if (refreshInFlight) {
        return refreshInFlight;
    }
    refreshInFlight = (async () => {
        const ok = await refreshAccessToken();
        refreshInFlight = null;
        return ok;
    })();
    return refreshInFlight;
};
